import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    ColumnDef,
    ColumnFiltersState,
    PaginationState,
    SortingState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, MoreHorizontal, Plus, Edit, Archive } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trpc } from '~/api/trpc';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';
import Content from '../components/layout/Content';
import { Skeleton } from '../components/ui/skeleton';
import { useShallow } from 'zustand/react/shallow';
import { useTreatmentsTableFilteringStore } from '~/stores/treatments-table-filtering-store';
import { toast } from 'sonner';

// Define the Product type based on the PhysicalProduct model
export type Product = {
    id: string;
    createdAt: string;
    name: string;
    description: string;
    unitPriceGross: number | string;
    unitPriceNet: number | string;
    isActive: boolean;
};

// Product Form Dialog Component
const ProductDialog = ({ 
    product, 
    isOpen, 
    onOpenChange, 
    mode 
}: { 
    product?: Product; 
    isOpen: boolean; 
    onOpenChange: (open: boolean) => void;
    mode: 'create' | 'edit';
}) => {
    const [formData, setFormData] = useState({
        name: product?.name || '',
        description: product?.description || '',
        unitPriceGross: Number(product?.unitPriceGross) || 0,
    });

    const utils = trpc.useUtils();
    const createMutation = trpc.physicalProducts.create.useMutation();
    const updateMutation = trpc.physicalProducts.update.useMutation();

    // Reset form when product changes or dialog opens/closes
    useEffect(() => {
        if (isOpen) {
            setFormData({
                name: product?.name || '',
                description: product?.description || '',
                unitPriceGross: Number(product?.unitPriceGross) || 0,
            });
        }
    }, [product, isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        try {
            if (mode === 'create') {
                await createMutation.mutateAsync(formData);
                toast.success('Produkt erfolgreich erstellt');
            } else {
                await updateMutation.mutateAsync({
                    id: product!.id,
                    ...formData
                });
                toast.success('Produkt erfolgreich aktualisiert');
            }
            
            // Invalidate and refetch queries
            await utils.physicalProducts.getAll.invalidate();
            onOpenChange(false);
        } catch (error) {
            toast.error(`Fehler beim ${mode === 'create' ? 'Erstellen' : 'Aktualisieren'} des Produkts`);
        }
    };

    const isLoading = createMutation.isPending || updateMutation.isPending;

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[525px]">
                <DialogHeader>
                    <DialogTitle>
                        {mode === 'create' ? 'Neues Produkt erstellen' : 'Produkt bearbeiten'}
                    </DialogTitle>
                    <DialogDescription>
                        {mode === 'create' 
                            ? 'Geben Sie die Details für das neue Produkt ein.' 
                            : 'Ändern Sie die Produktdetails.'
                        }
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="name">Name</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Produktname eingeben"
                                required
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="description">Beschreibung</Label>
                            <Textarea
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Produktbeschreibung eingeben"
                                rows={3}
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="unitPriceGross">Bruttopreis (EUR)</Label>
                            <Input
                                id="unitPriceGross"
                                type="number"
                                min="0"
                                step="0.01"
                                value={formData.unitPriceGross}
                                onChange={(e) => setFormData(prev => ({ ...prev, unitPriceGross: parseFloat(e.target.value) || 0 }))}
                                placeholder="Bruttopreis in EUR eingeben"
                                required
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isLoading}
                        >
                            Abbrechen
                        </Button>
                        <Button type="submit" disabled={isLoading}>
                            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {mode === 'create' ? 'Erstellen' : 'Aktualisieren'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

// Archive Confirmation Dialog
const ArchiveProductDialog = ({ 
    product, 
    isOpen, 
    onOpenChange 
}: { 
    product?: Product; 
    isOpen: boolean; 
    onOpenChange: (open: boolean) => void;
}) => {
    const utils = trpc.useUtils();
    const archiveMutation = trpc.physicalProducts.archive.useMutation();

    const handleArchive = async () => {
        if (!product) return;
        
        try {
            await archiveMutation.mutateAsync({ id: product.id });
            toast.success('Produkt erfolgreich archiviert');
            await utils.physicalProducts.getAll.invalidate();
            onOpenChange(false);
        } catch (error) {
            toast.error('Fehler beim Archivieren des Produkts');
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Produkt archivieren</DialogTitle>
                    <DialogDescription>
                        Sind Sie sicher, dass Sie "{product?.name}" archivieren möchten? Das Produkt wird aus allen Listen ausgeblendet, aber bestehende Rechnungen bleiben erhalten.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={archiveMutation.isPending}
                    >
                        Abbrechen
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleArchive}
                        disabled={archiveMutation.isPending}
                    >
                        {archiveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Archivieren
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// Create Product Button (only rendered for ADMIN users)
const CreateProductButton = () => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const role = useUserStore((state) => state.user?.role);

    if (role !== UserRole.ADMIN) return null;

    return (
        <>
            <Button
                variant='outline'
                className='border-primary-400 text-primary-400 hover:text-primary-400 hover:bg-primary-400/10 transition-all'
                onClick={() => setIsDialogOpen(true)}
            >
                <Plus className='mr-1 h-4 w-4' />
                Neues Produkt
            </Button>
            <ProductDialog
                mode="create"
                isOpen={isDialogOpen}
                onOpenChange={setIsDialogOpen}
            />
        </>
    );
};

// Table columns definition
export const columns: ColumnDef<Product>[] = [
    {
        accessorKey: 'name',
        header: ({ column }) => {
            return (
                <Button
                    variant='ghost'
                    onClick={() => {
                        const currentSort = column.getIsSorted();
                        if (currentSort === 'asc') {
                            column.toggleSorting(true);
                        } else if (currentSort === 'desc') {
                            column.clearSorting();
                        } else {
                            column.toggleSorting(false);
                        }
                    }}
                >
                    Name
                    {column.getIsSorted() === 'asc' ? (
                        <ArrowUp className='ml-2 h-4 w-4' />
                    ) : column.getIsSorted() === 'desc' ? (
                        <ArrowDown className='ml-2 h-4 w-4' />
                    ) : (
                        <ArrowUpDown className='ml-2 h-4 w-4 opacity-50' />
                    )}
                </Button>
            );
        },
    },
    {
        accessorKey: 'description',
        header: 'Beschreibung',
        cell: ({ row }) => {
            const description = row.original.description;
            // Truncate long descriptions
            return <div className='max-w-xs truncate'>{description || '-'}</div>;
        },
    },
    {
        accessorKey: 'unitPriceGross',
        header: 'Bruttopreis',
        cell: ({ row }) => {
            const priceGross = Number(row.original.unitPriceGross);
            return <div>€{priceGross.toFixed(2)}</div>;
        },
    },
    {
        accessorKey: 'unitPriceNet',
        header: 'Nettopreis',
        cell: ({ row }) => {
            const priceNet = Number(row.original.unitPriceNet);
            return <div>€{priceNet.toFixed(2)}</div>;
        },
    },
    {
        id: 'createdAt',
        header: 'Erstellt am',
        cell: ({ row }) => {
            const date = new Date(row.original.createdAt);
            return <div>{format(date, 'PPP')}</div>;
        },
        accessorFn: (row) => new Date(row.createdAt).getTime(),
    },
    {
        id: 'actions',
        cell: ({ row }) => {
            const product = row.original;
            const [editDialogOpen, setEditDialogOpen] = useState(false);
            const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
            const role = useUserStore((state) => state.user?.role);

            if (role !== UserRole.ADMIN) return null;

            return (
                <>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant='ghost' className='h-8 w-8 p-0'>
                                <span className='sr-only'>Menü öffnen</span>
                                <MoreHorizontal className='h-4 w-4' />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end'>
                            <DropdownMenuLabel>Aktionen</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => navigator.clipboard.writeText(product.id)}>
                                Produkt-ID kopieren
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setEditDialogOpen(true)}>
                                <Edit className='mr-2 h-4 w-4' />
                                Produkt bearbeiten
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                                onClick={() => setArchiveDialogOpen(true)}
                                className='text-red-600 focus:text-red-600'
                            >
                                <Archive className='mr-2 h-4 w-4' />
                                Produkt archivieren
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    
                    <ProductDialog
                        product={product}
                        mode="edit"
                        isOpen={editDialogOpen}
                        onOpenChange={setEditDialogOpen}
                    />
                    
                    <ArchiveProductDialog
                        product={product}
                        isOpen={archiveDialogOpen}
                        onOpenChange={setArchiveDialogOpen}
                    />
                </>
            );
        },
    },
];

// Data Table Props Interface
interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    manualPagination?: boolean;
    manualSorting?: boolean;
    manualFiltering?: boolean;
    syncWithUrl?: boolean;
}

// Main Data Table Component
export const DataTable = <TData, TValue>({
    columns,
    manualPagination = true,
    manualSorting = true,
    manualFiltering = true,
    syncWithUrl = true,
}: DataTableProps<TData, TValue>) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const isInitialized = useRef<boolean>(false);

    // Get state and setters from Zustand store
    const {
        sorting: storeSorting,
        columnFilters: storeColumnFilters,
        pagination: storePagination,
        setSorting,
        setColumnFilters,
        setPagination,
    } = useTreatmentsTableFilteringStore(
        useShallow((state) => ({
            sorting: state.sorting,
            setSorting: state.setSorting,
            columnFilters: state.columnFilters,
            setColumnFilters: state.setColumnFilters,
            pagination: state.pagination,
            setPagination: state.setPagination,
        }))
    );

    // Initial State Synchronization Effect
    useEffect(() => {
        if (!syncWithUrl || isInitialized.current) {
            return;
        }

        isInitialized.current = true;
        const newParams = new URLSearchParams(searchParams);
        let needsUrlUpdate = false;

        // Sync Sorting
        const urlSortField = searchParams.get('sort');
        const urlSortDir = searchParams.get('dir') || 'asc';
        let currentSorting = storeSorting;

        if (urlSortField) {
            const urlSortingState: SortingState = [{ id: urlSortField, desc: urlSortDir === 'desc' }];
            if (JSON.stringify(urlSortingState) !== JSON.stringify(storeSorting)) {
                setSorting(urlSortingState);
                currentSorting = urlSortingState;
            }
            if (!searchParams.has('dir') && urlSortField) {
                newParams.set('dir', 'asc');
                needsUrlUpdate = true;
            }
        } else if (storeSorting.length > 0) {
            newParams.set('sort', storeSorting[0].id);
            newParams.set('dir', storeSorting[0].desc ? 'desc' : 'asc');
            needsUrlUpdate = true;
        } else {
            if (searchParams.has('sort')) { newParams.delete('sort'); needsUrlUpdate = true; }
            if (searchParams.has('dir')) { newParams.delete('dir'); needsUrlUpdate = true; }
        }

        // Sync Filtering
        const urlFilters: ColumnFiltersState = [];
        let hasUrlFilters = false;

        for (const [key, value] of searchParams.entries()) {
            if (key.startsWith('filter_')) {
                hasUrlFilters = true;
                urlFilters.push({
                    id: key.replace('filter_', ''),
                    value: value,
                });
            }
        }

        let currentFilters = storeColumnFilters;
        if (hasUrlFilters) {
            if (JSON.stringify(urlFilters.sort((a, b) => a.id.localeCompare(b.id))) !== JSON.stringify(storeColumnFilters.sort((a, b) => a.id.localeCompare(b.id)))) {
                setColumnFilters(urlFilters);
                currentFilters = urlFilters;
            }
        } else if (storeColumnFilters.length > 0) {
            storeColumnFilters.forEach(filter => {
                newParams.set(`filter_${filter.id}`, String(filter.value));
            });
            needsUrlUpdate = true;
        } else {
            Array.from(searchParams.keys())
                .filter(key => key.startsWith('filter_'))
                .forEach(key => {
                    newParams.delete(key);
                    needsUrlUpdate = true;
                });
        }

        // Sync Pagination
        const urlPage = searchParams.get('page');
        const urlSize = searchParams.get('size');
        let currentPagination = storePagination;

        const paginationUpdateFromUrl: Partial<PaginationState> = {};
        let urlHasPagination = false;

        if (urlPage !== null) {
            const pageIndex = Math.max(0, parseInt(urlPage, 10) || 0);
            paginationUpdateFromUrl.pageIndex = pageIndex;
            urlHasPagination = true;
        }

        if (urlSize !== null) {
            const pageSize = Math.max(1, parseInt(urlSize, 10) || storePagination.pageSize);
            paginationUpdateFromUrl.pageSize = pageSize;
            urlHasPagination = true;
        }

        if (urlHasPagination) {
            const newPaginationState = { ...storePagination, ...paginationUpdateFromUrl };
            if (newPaginationState.pageIndex !== storePagination.pageIndex || newPaginationState.pageSize !== storePagination.pageSize) {
                setPagination(newPaginationState);
                currentPagination = newPaginationState;
            }
            if (String(currentPagination.pageIndex) !== urlPage) {
                newParams.set('page', String(currentPagination.pageIndex));
                needsUrlUpdate = true;
            }
            if (String(currentPagination.pageSize) !== urlSize) {
                newParams.set('size', String(currentPagination.pageSize));
                needsUrlUpdate = true;
            }
        } else {
            newParams.set('page', String(storePagination.pageIndex));
            newParams.set('size', String(storePagination.pageSize));
            needsUrlUpdate = true;
        }

        if (needsUrlUpdate) {
            setSearchParams(newParams, { replace: true });
        }
    }, [syncWithUrl, searchParams, setSearchParams]);

    // Derive API Query Params from the URL
    const queryPage = parseInt(searchParams.get('page') || String(storePagination.pageIndex), 10);
    const queryPageSize = parseInt(searchParams.get('size') || String(storePagination.pageSize), 10);
    const querySortField = searchParams.get('sort') || undefined;
    const querySortDirection = (searchParams.get('dir') || undefined) as 'asc' | 'desc' | undefined;

    const queryFilters: Record<string, string | undefined> = {};
    for (const [key, value] of searchParams.entries()) {
        if (key.startsWith('filter_')) {
            queryFilters[key.replace('filter_', '')] = value;
        }
    }

    // Data Fetching
    const {
        data: productsResponse,
        isLoading: isQueryLoading,
        isFetching: isQueryFetching,
    } = trpc.physicalProducts.getAll.useQuery(
        {
            page: queryPage,
            pageSize: queryPageSize,
            sortField: querySortField,
            sortDirection: querySortDirection,
            filters: queryFilters,
        },
        {
            suspense: true,
        }
    );

    const isLoading = isQueryLoading;
    const isFetching = isQueryFetching;

    // State Synchronization Effects (State -> URL)
    useEffect(() => {
        if (!syncWithUrl || !isInitialized.current) return;

        const currentSortField = searchParams.get('sort');
        const currentSortDir = searchParams.get('dir');
        const storeSortField = storeSorting[0]?.id;
        const storeSortDir = storeSorting[0]?.desc ? 'desc' : 'asc';

        if (storeSortField !== currentSortField || (storeSortField && storeSortDir !== currentSortDir)) {
            const newParams = new URLSearchParams(searchParams);
            if (storeSorting.length > 0) {
                newParams.set('sort', storeSorting[0].id);
                newParams.set('dir', storeSorting[0].desc ? 'desc' : 'asc');
            } else {
                newParams.delete('sort');
                newParams.delete('dir');
            }
            setSearchParams(newParams, { replace: true });
        }
    }, [storeSorting, syncWithUrl, searchParams, setSearchParams]);

    useEffect(() => {
        if (!syncWithUrl || !isInitialized.current) return;

        const newParams = new URLSearchParams(searchParams);
        let filtersChanged = false;

        const urlFiltersMap = new Map<string, string>();
        Array.from(newParams.keys())
            .filter((key) => key.startsWith('filter_'))
            .forEach((key) => urlFiltersMap.set(key.replace('filter_', ''), newParams.get(key)!));

        const storeFiltersMap = new Map<string, string>();
        storeColumnFilters.forEach(f => storeFiltersMap.set(f.id, String(f.value)));

        urlFiltersMap.forEach((_, id) => {
            if (!storeFiltersMap.has(id)) {
                newParams.delete(`filter_${id}`);
                filtersChanged = true;
            }
        });

        storeFiltersMap.forEach((value, id) => {
            if (urlFiltersMap.get(id) !== value) {
                newParams.set(`filter_${id}`, value);
                filtersChanged = true;
            }
        });

        if (filtersChanged) {
            setSearchParams(newParams, { replace: true });
        }
    }, [storeColumnFilters, syncWithUrl, searchParams, setSearchParams]);

    useEffect(() => {
        if (!syncWithUrl || !isInitialized.current) return;

        const currentPage = searchParams.get('page');
        const currentSize = searchParams.get('size');
        const storePage = String(storePagination.pageIndex);
        const storeSize = String(storePagination.pageSize);

        if (currentPage !== storePage || currentSize !== storeSize) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('page', storePage);
            newParams.set('size', storeSize);
            setSearchParams(newParams, { replace: true });
        }
    }, [storePagination, syncWithUrl, searchParams, setSearchParams]);

    // React Table Instance
    const [rowSelection, setRowSelection] = useState({});

    const table = useReactTable({
        data: productsResponse?.data ?? [],
        columns,
        pageCount: productsResponse?.pageCount ?? -1,
        state: {
            sorting: storeSorting,
            columnFilters: storeColumnFilters,
            pagination: storePagination,
            rowSelection,
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onRowSelectionChange: setRowSelection,
        manualPagination,
        manualSorting,
        manualFiltering,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: manualPagination ? undefined : getPaginationRowModel(),
        getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
        getFilteredRowModel: manualFiltering ? undefined : getFilteredRowModel(),
    });

    // Clamp Page Index Effect
    useEffect(() => {
        if (!productsResponse || isLoading || isFetching || !manualPagination) {
            return;
        }

        const currentPageIndex = table.getState().pagination.pageIndex;
        const pageCount = productsResponse.pageCount;

        if (pageCount > 0 && currentPageIndex >= pageCount) {
            table.setPageIndex(Math.max(0, pageCount - 1));
        }
    }, [productsResponse, table, isLoading, isFetching, manualPagination]);

    // Loading Skeleton
    if (isLoading) {
        return (
            <div className='bg-white rounded-lg shadow-sm p-6'>
                <div className='flex items-center justify-between py-4'>
                    <Skeleton className='h-10 w-[250px]' />
                    <Skeleton className='h-10 w-[150px]' />
                </div>
                <div className='rounded-md border border-gray-200'>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {Array.from({ length: columns.length }).map((_, index) => (
                                    <TableHead key={index}>
                                        <Skeleton className='h-6 w-full' />
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {Array.from({ length: table.getState().pagination.pageSize }).map((_, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {Array.from({ length: columns.length }).map((_, colIndex) => (
                                        <TableCell key={colIndex}>
                                            <Skeleton className='h-6 w-full' />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
                <div className='flex items-center justify-between space-x-2 py-4 mt-2'>
                    <Skeleton className='h-6 w-24' />
                    <div className="flex space-x-2">
                        <Skeleton className='h-9 w-20' />
                        <Skeleton className='h-9 w-20' />
                    </div>
                </div>
            </div>
        );
    }

    // Main Table Render
    return (
        <div className='bg-white rounded-lg shadow-sm p-6'>
            {/* Filter Input & Add Button */}
            <div className='flex items-center justify-between py-4'>
                <div className='flex gap-4 items-center'>
                    <Input
                        placeholder='Suchen'
                        value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
                        onChange={(event) => {
                            table.getColumn('name')?.setFilterValue(event.target.value)
                        }}
                        className='max-w-sm focus:ring-primary'
                    />
                    {isFetching && (
                        <div className='flex items-center text-sm text-muted-foreground'>
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            Laden...
                        </div>
                    )}
                </div>
                <CreateProductButton />
            </div>

            {/* Table */}
            <div className='rounded-md border border-gray-200 overflow-hidden'>
                <Table>
                    <TableHeader className='bg-gray-50'>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <TableHead key={header.id} className='font-medium text-gray-700'>
                                            {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-state={row.getIsSelected() && 'selected'}
                                    className='hover:bg-gray-50 transition-colors'
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id} style={{ width: cell.column.getSize() !== 150 ? cell.column.getSize() : undefined }}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className='h-24 text-center'>
                                    {isFetching ? (
                                        <div className='flex justify-center items-center'>
                                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                            Daten werden geladen...
                                        </div>
                                    ) : (
                                        queryFilters && Object.keys(queryFilters).length > 0
                                            ? 'Keine Produkte gefunden, die Ihren Filtern entsprechen.'
                                            : 'Keine Produkte gefunden.'
                                    )}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination Controls */}
            <div className='flex items-center justify-between space-x-2 py-4 mt-2'>
                <div className='flex items-center space-x-2'>
                    <select
                        value={table.getState().pagination.pageSize}
                        onChange={e => {
                            table.setPageSize(Number(e.target.value))
                        }}
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                        disabled={isFetching}
                    >
                        {[10, 20, 30, 40, 50].map(pageSize => (
                            <option key={pageSize} value={pageSize}>
                                Zeige {pageSize}
                            </option>
                        ))}
                    </select>
                    <div className='text-sm text-muted-foreground'>
                        Seite {table.getState().pagination.pageIndex + 1} von {table.getPageCount() > 0 ? table.getPageCount() : 1}
                    </div>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage() || isFetching}
                        className='border-gray-300 hover:bg-gray-50'
                    >
                        Zurück
                    </Button>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage() || isFetching}
                        className='border-gray-300 hover:bg-gray-50'
                    >
                        Weiter
                    </Button>
                </div>
            </div>
        </div>
    );
};

// Main Products component
const Products = () => {
    return (
        <Content>
            <div className='container mx-auto py-10'>
                <h1 className='text-2xl font-bold mb-6'>Produkte</h1>
                <DataTable 
                    columns={columns} 
                    manualPagination={true} 
                    manualSorting={true} 
                    manualFiltering={true} 
                    syncWithUrl={true} 
                />
            </div>
        </Content>
    );
};

export default Products;
